import { Paper, Typography } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { ItemState } from "../../redux/state";
import {
  AppBarDirtyIndicator,
  AppBarSaveButton,
  AppBarValidationIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { QualApprovalStatus, Qualification } from "../../domain/Qualification";
import { deleteSelectedAttachment, updatingItem } from "../../redux/actions";
import { ModuleAppBar } from "../ModuleAppBar";
import { QualificationDetails } from "../sections/QualificationDetails";
import { DateDetails } from "../sections/DateDetails";
import { SectionProps } from "../sections/SectionProps";
import { Attachment } from "../../domain/Attachment";
import { qualificationService } from "../../services";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { ManageQualRoles, UserRole } from "../../domain/UserRole";
import { ApproveButtonAndDialog } from "./ApproveButtonAndDialog";
import { RejectButtonAndDialog } from "./RejectButtonAndDialog";
import { Prompt } from "react-router-dom";
import { authSelector } from "../../redux/selectors";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

export const ItemDetails: React.FC<{
  state: ItemState;
}> = ({ state }) => {
  const item = state.item!;

  const dispatch = useDispatch();
  const { isInRole, userType, token } = useAuth(authSelector);

  const canEditQual = isInRole(...ManageQualRoles);
  const isOfqualApproval =
    item.approvalStatus === QualApprovalStatus.AwaitingOfqualApproval;
  const isCceaApproval =
    item.approvalStatus === QualApprovalStatus.AwaitingCceaApproval;

  const canApproveQual =
    isInRole(UserRole.ITAdmin) ||
    (isInRole(UserRole.CCEAAccreditationAdmin) && isCceaApproval) ||
    (isInRole(UserRole.OfqualAccreditationAdmin) && isOfqualApproval);

  const isPendingApproval = isOfqualApproval || isCceaApproval;

  const disabled =
    state.status === "UPDATING" || !canEditQual || isPendingApproval;

  const {
    handleSubmit,
    control,
    formState,
    errors,
    getValues,
    setError,
    clearErrors,
    setValue,
  } = useForm<Qualification>({
    defaultValues: item,
    shouldUnregister: false,
  });

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  let { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (newModel: Qualification) => {
    const fieldsToIgnore: (keyof Qualification)[] = ["files"];

    if (dirtyFields["qualLevel"] === true) {
      dirtyFields = {
        ...dirtyFields,
        qualLevelId: true,
        qualSubLevel: true,
      };
    }

    if (dirtyFields["qualSubLevel"] === true) {
      dirtyFields = {
        ...dirtyFields,
        qualLevelId: true,
      };
    }

    const changedFields = (Object.keys(
      dirtyFields
    ) as (keyof Qualification)[]).filter((x) => !fieldsToIgnore.includes(x));

    const document = modelToJsonPatchDocument(newModel, changedFields);
    const meta = buildMetaData(newModel, item, changedFields, lookupTracker);

    dispatch(
      updatingItem(
        item.id,
        { document, meta },
        state.attachmentsToDelete,
        newModel.files
      )
    );
  };

  const deleteAttachment = (attachment: Attachment) =>
    dispatch(deleteSelectedAttachment(attachment));

  const downloadAttachment = (blobId: string): Promise<Blob> =>
    qualificationService.downloadAttachment(item!.id, blobId, { token });

  const classes = useStyles();

  const sectionProps: SectionProps<Qualification> = {
    user: { userType, token, isInRole },
    disabled,
    control,
    errors,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: keyof Qualification, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    lookupTracker,
    item,
    deleteAttachment,
    downloadAttachment,
  };

  return (
    <>
      <div>
        <Prompt
          message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
          when={Object.keys(dirtyFields).length > 0}
        />
        <ModuleAppBar breadcrumbs={[{ label: `${item.title}` }]}>
          <AppBarDirtyIndicator dirtyFields={dirtyFields} />
          <AppBarValidationIndicator errors={errors} />
          {canApproveQual && isPendingApproval ? (
            <>
              <ApproveButtonAndDialog item={item} />
              <RejectButtonAndDialog item={item} />
            </>
          ) : null}
          <AppBarSaveButton
            onClick={handleSubmit(onSubmit)}
            dataTest="save"
            isSubmitting={state.status === "UPDATING"}
            isSubmitted={state.lastCrudResult.isSuccess}
            disabled={disabled}
          />
        </ModuleAppBar>

        <Typography variant="h6">Qualification details</Typography>

        <Paper className={classes.formSection}>
          <QualificationDetails {...sectionProps} />
        </Paper>

        <Typography variant="h6">Dates</Typography>

        <Paper className={classes.formSection}>
          <DateDetails shouldValidateOpStart={false} {...sectionProps} />
        </Paper>
      </div>
    </>
  );
};
